import React, { type FC } from 'react';

import { Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';

import { RowInteractionWrapper } from '../../../RowInteractionWrapper.tsx';
import { useExpandIconRenderer } from '../../../hooks/useExpandIconRenderer.tsx';

import { PrincipalDetails, type PrincipalDetailsProps } from './PrincipalDetails.tsx';
import { PrincipalAccess, type PrincipalAccessProps } from './PrincipalAccess.tsx';

const inlineStyle = xcss({
	paddingBlock: 'space.100',
	paddingInline: 'space.100',
	':hover': {
		background: token('elevation.surface.hovered'),
	},
	borderRadius: 'radius.small',
});

export type SpecificAccessRowProps = {
	key: string;
	principalDetails: PrincipalDetailsProps;
	principalAccess: PrincipalAccessProps;
	isExpanded: boolean;
	isSpecificAccessDialogOpen: boolean;
	onExpandClick: () => void;
	onOpenSpecificAccessDialog: () => void;
};

export const SpecificAccessRow: FC<SpecificAccessRowProps> = ({
	principalDetails,
	principalAccess,
	isExpanded,
	isSpecificAccessDialogOpen,
	onExpandClick,
	onOpenSpecificAccessDialog,
}) => {
	const { renderIcon } = useExpandIconRenderer({
		isExpanded,
		isSpecificAccessDialogOpen,
		fallbackComponent: <PrincipalAccess {...principalAccess} />,
	});

	if (!expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
		return (
			<Inline grow="fill" spread="space-between" alignBlock="center" space="space.150">
				<PrincipalDetails {...principalDetails} />
				<PrincipalAccess {...principalAccess} />
			</Inline>
		);
	}

	return (
		<RowInteractionWrapper
			isExpanded={isExpanded}
			onExpandClick={onExpandClick}
			onOpenSpecificAccessDialog={onOpenSpecificAccessDialog}
			isSpecificAccessDialogOpen={isSpecificAccessDialogOpen}
		>
			<Inline
				grow="fill"
				spread="space-between"
				alignBlock="center"
				space="space.150"
				xcss={!isSpecificAccessDialogOpen && inlineStyle}
			>
				<PrincipalDetails {...principalDetails} />
				{renderIcon()}
			</Inline>
		</RowInteractionWrapper>
	);
};
